import {
  Bus,
  BellRing,
  Volume2,
  ShieldCheck,
  Compass,
  Route,
  ArrowLeftRight,
  Truck,
  Users,
  ReceiptText,
  BarChart3,
  CalendarDays,
  Layers,
  CalendarOff,
  Siren,
  Mic,
  Wifi,
  Sparkles,
  Library,
  MessageSquareText,
  SlidersHorizontal,
  Zap,
  Lock,
  Scale,
  BadgeCheck,
  Radio,
  LineChart,
  ToggleLeft,
  FileText,
  Link2,
  Search,
  MessagesSquare,
  Repeat,
  type LucideIcon,
} from 'lucide-react'

/**
 * Single source of truth for the Tdrem product catalogue.
 *
 * Copy (names, taglines, features, FAQ…) lives in the `products` i18n
 * namespace under the same `id`; this file holds everything that is NOT
 * language-dependent: routes, live app URLs, status, platforms, icons,
 * accent colours and screenshots.
 *
 * To add a product: add an entry here + a block in each
 * public/locales/<lng>/products.json.
 */

export type ProductId = 'bms' | 'bell' | 'mosque' | 'crypto' | 'masar'
export type Audience = 'transport' | 'school' | 'mosque' | 'crypto' | 'careers'
export type Platform = 'web' | 'windows' | 'mobile'
/** live = generally available · beta = open beta · comingSoon = private beta / waitlist */
export type ProductStatus = 'live' | 'beta' | 'comingSoon'

export interface ProductAccent {
  /** Solid fill (icon tiles, primary buttons). */
  solid: string
  /** Hover state for solid fill. */
  solidHover: string
  /** Very soft tinted background. */
  soft: string
  /** Accent-coloured text. */
  text: string
  /** Gradient stops, used with bg-gradient-to-*. */
  gradient: string
  /** Blurred glow behind visuals. */
  glow: string
  /** Subtle accent border. */
  border: string
}

export interface ProductScreen {
  src: string
  /** Controls the device frame used when rendering. */
  frame: 'desktop' | 'phone' | 'plain'
  /** i18n key under `products:<id>.screens.<key>` for the caption. */
  key: string
}

export interface Product {
  id: ProductId
  slug: string
  icon: LucideIcon
  status: ProductStatus
  /** Live app (sign-in) URL. */
  appUrl: string
  /** Human-readable host shown in the UI. */
  appHost: string
  platforms: Platform[]
  audience: Audience
  accent: ProductAccent
  /** One icon per entry in `products:<id>.features` (same order). */
  featureIcons: LucideIcon[]
  /** Real screenshots, if we have them. Products without screenshots use an illustrated preview. */
  screens: ProductScreen[]
}

export const PRODUCTS: Product[] = [
  {
    id: 'bms',
    slug: 'bms',
    icon: Bus,
    status: 'live',
    appUrl: 'https://www.bms.tdrem.com',
    appHost: 'bms.tdrem.com',
    platforms: ['web', 'mobile'],
    audience: 'transport',
    accent: {
      solid: 'bg-blue-600',
      solidHover: 'hover:bg-blue-700',
      soft: 'bg-blue-50 dark:bg-blue-500/10',
      text: 'text-blue-600 dark:text-blue-400',
      gradient: 'from-blue-600 to-sky-500',
      glow: 'bg-blue-500/25',
      border: 'border-blue-200 dark:border-blue-500/30',
    },
    featureIcons: [Route, ArrowLeftRight, Truck, Users, ReceiptText, BarChart3],
    screens: [
      { src: '/images/bms/dashboard_1.png', frame: 'desktop', key: 'dashboard' },
      { src: '/images/bms/trip_management_1.png', frame: 'desktop', key: 'trips' },
      { src: '/images/bms/assignment_management.png', frame: 'desktop', key: 'assignments' },
      { src: '/images/bms/offer_management_1.png', frame: 'desktop', key: 'offers' },
      { src: '/images/bms/reports_1.png', frame: 'desktop', key: 'reports' },
      { src: '/images/bms/mobile_1.png', frame: 'phone', key: 'mobile' },
    ],
  },
  {
    id: 'bell',
    slug: 'school-bell',
    icon: BellRing,
    status: 'live',
    appUrl: 'https://bell.tdrem.com',
    appHost: 'bell.tdrem.com',
    platforms: ['windows', 'web', 'mobile'],
    audience: 'school',
    accent: {
      solid: 'bg-violet-600',
      solidHover: 'hover:bg-violet-700',
      soft: 'bg-violet-50 dark:bg-violet-500/10',
      text: 'text-violet-600 dark:text-violet-400',
      gradient: 'from-violet-600 to-indigo-500',
      glow: 'bg-violet-500/25',
      border: 'border-violet-200 dark:border-violet-500/30',
    },
    featureIcons: [CalendarDays, Layers, CalendarOff, Siren, Mic, Wifi],
    screens: [
      { src: '/images/tdrem-bell/mobile-control.webp', frame: 'phone', key: 'mobile' },
      { src: '/images/tdrem-bell/audio-library.webp', frame: 'phone', key: 'audio' },
      { src: '/images/tdrem-bell/schedule-manager.webp', frame: 'phone', key: 'schedule' },
      { src: '/images/tdrem-bell/how-it-works.webp', frame: 'plain', key: 'diagram' },
    ],
  },
  {
    id: 'mosque',
    slug: 'mosque',
    icon: Volume2,
    status: 'live',
    appUrl: 'https://mosque.tdrem.com',
    appHost: 'mosque.tdrem.com',
    platforms: ['windows', 'mobile', 'web'],
    audience: 'mosque',
    accent: {
      solid: 'bg-emerald-600',
      solidHover: 'hover:bg-emerald-700',
      soft: 'bg-emerald-50 dark:bg-emerald-500/10',
      text: 'text-emerald-600 dark:text-emerald-400',
      gradient: 'from-emerald-600 to-teal-500',
      glow: 'bg-emerald-500/25',
      border: 'border-emerald-200 dark:border-emerald-500/30',
    },
    featureIcons: [Sparkles, Library, MessageSquareText, SlidersHorizontal, Zap, Lock],
    screens: [
      { src: '/images/mosque/control-panel.webp', frame: 'phone', key: 'control' },
      { src: '/images/mosque/voice-library.webp', frame: 'phone', key: 'voice' },
      { src: '/images/mosque/recording.webp', frame: 'phone', key: 'recording' },
      { src: '/images/mosque/how-it-works.webp', frame: 'plain', key: 'diagram' },
    ],
  },
  {
    id: 'crypto',
    slug: 'crypto',
    icon: ShieldCheck,
    status: 'beta',
    appUrl: 'https://crypto.tdrem.com',
    appHost: 'crypto.tdrem.com',
    platforms: ['web', 'mobile'],
    audience: 'crypto',
    accent: {
      solid: 'bg-amber-500',
      solidHover: 'hover:bg-amber-600',
      soft: 'bg-amber-50 dark:bg-amber-500/10',
      text: 'text-amber-600 dark:text-amber-400',
      gradient: 'from-amber-500 to-orange-500',
      glow: 'bg-amber-500/25',
      border: 'border-amber-200 dark:border-amber-500/30',
    },
    featureIcons: [ShieldCheck, Scale, BadgeCheck, Radio, LineChart, ToggleLeft],
    screens: [],
  },
  {
    id: 'masar',
    slug: 'masar-ai',
    icon: Compass,
    status: 'comingSoon',
    appUrl: 'https://masarai.tdrem.com',
    appHost: 'masarai.tdrem.com',
    platforms: ['web', 'mobile'],
    audience: 'careers',
    accent: {
      solid: 'bg-rose-600',
      solidHover: 'hover:bg-rose-700',
      soft: 'bg-rose-50 dark:bg-rose-500/10',
      text: 'text-rose-600 dark:text-rose-400',
      gradient: 'from-rose-600 to-fuchsia-500',
      glow: 'bg-rose-500/25',
      border: 'border-rose-200 dark:border-rose-500/30',
    },
    featureIcons: [FileText, Link2, Search, MessagesSquare, Repeat, Lock],
    screens: [],
  },
]

export const AUDIENCES: Audience[] = ['school', 'mosque', 'transport', 'careers', 'crypto']

export const getProductBySlug = (slug: string) => PRODUCTS.find((p) => p.slug === slug)
export const getProductById = (id: string) => PRODUCTS.find((p) => p.id === id)
